import { useMemo, useState } from 'react';
import { ApiError } from '@nyce/admin-kit/api';
import { Button, Chip, Dialog, TextField } from '@nyce/admin-kit/ui';
import {
  ActionsRow,
  ConfirmButton,
  EmptyState,
  ErrorBox,
  Loading,
  Page,
  SortSelect,
  SortTh,
  TableScroller,
  useAsync,
  useNotice,
  useSort,
} from '../../components/common';
import { useParam } from '../../pages/statistics/shared';
import { formatDateTime, formatRelative } from '../../lib/format';
import { useTargets } from '../../identity/session';
import { printQueue, type PrintServerClient, type TenantAuth, type TenantAuthMode } from '../api/client';
import { linkOf, usePrintQueueLinks } from '../links';
import { TenantCell, usePrintQueueTarget, usePrintQueueWrite } from '../shared';
import '../printqueue.css';

/** How a tenant signs in: its mode, and whether AUTH tokens were actually seen. */
export function TenantAuthChip({ t }: { t: TenantAuth }) {
  const [tone, label] =
    t.mode === 'Auth'
      ? ['green', 'AUTH only']
      : t.lastBearerAt
        ? ['green', 'AUTH']
        : t.mode === 'Both'
          ? ['amber', 'waiting for AUTH']
          : ['grey', 'tokens'];
  return (
    <Chip
      size="sm"
      className={`oph-status nyk-tone-${tone}`}
      data-auth={t.mode}
      title={`Mode ${t.mode}${t.lastBearerAt ? `; last AUTH token ${formatRelative(t.lastBearerAt)}` : ''}`}
    >
      {label}
    </Chip>
  );
}

const when = (t: string | null) => (t ? `${formatRelative(t)}` : '—');

/**
 * Tenants' move to nyce-logic-auth (`docs/printqueue-auth.md`): which tenants still use print queue tokens, which
 * already call with AUTH tokens, and per tenant Require AUTH (its print queue tokens are then refused — WorkServer and
 * print servers alike) or back. A print server gets its own AUTH client here, shown once.
 */
export function PrintQueueTenantsPage() {
  const target = usePrintQueueTarget();
  const api = useMemo(() => printQueue(target), [target]);
  const canWrite = usePrintQueueWrite();
  const links = usePrintQueueLinks(target);
  const { signedIn } = useTargets();
  const toast = useNotice();
  const data = useAsync(async () => {
    try {
      return { tenants: await api.tenantAuth(), unsupported: false };
    } catch (e) {
      if (e instanceof ApiError && e.status === 404)
        return { tenants: [] as TenantAuth[], unsupported: true };
      throw e;
    }
  }, [api]);
  const [client, setClient] = useState<{ tenantId: string; server: string } | null>(null);
  const [issued, setIssued] = useState<(PrintServerClient & { tenantId: string; server: string }) | null>(
    null
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sort = useSort(
    (data.data?.tenants ?? []).slice().sort((a, b) => a.tenantId.localeCompare(b.tenantId)),
    {
      tenant: { label: 'Tenant', value: (t) => t.tenantId },
      mode: { label: 'Sign-in', value: (t) => t.mode },
      legacy: { label: 'Last print queue token', value: (t) => t.lastLegacyAt, desc: true },
      bearer: { label: 'Last AUTH token', value: (t) => t.lastBearerAt, desc: true },
    },
    useParam('sort', '')
  );
  const rows = sort.rows;

  const setMode = async (t: TenantAuth, mode: TenantAuthMode) => {
    try {
      await api.setTenantAuthMode(t.tenantId, mode);
      toast.success(
        `${t.tenantId}: ${mode === 'Auth' ? 'AUTH required' : mode === 'Both' ? 'tokens and AUTH' : 'tokens'}`
      );
      await data.reload();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };
  const createClient = async () => {
    if (!client) return;
    const server = client.server.trim();
    if (!server) return setError('The print server name is required (its PrintServerName setting).');
    setBusy(true);
    setError(null);
    try {
      const r = await api.printServerClient(client.tenantId, server);
      setIssued({ ...r, tenantId: client.tenantId, server });
      setClient(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page
      title="Tenants"
      description="Each tenant's move to AUTH tokens. Allow AUTH, check that its WorkServer and print servers show up with AUTH tokens, then require AUTH: its print queue tokens stop working. Back to tokens at any time."
    >
      {toast.element}
      <ErrorBox error={data.error} />
      {data.data?.unsupported && (
        <EmptyState
          card
          title="This print queue does not support AUTH yet."
          hint="Tenant sign-in needs print queue 1.3.0 or later."
        />
      )}
      {!data.data?.unsupported && (
        <div className="nyk-card">
          <Loading loading={data.loading && !data.data} />
          <SortSelect sort={sort} />
          {!!rows.length && (
            <TableScroller>
              <table className="nyk-ui-table nyk-cards" data-testid="pq-tenant-auth">
                <thead>
                  <tr>
                    <SortTh sort={sort} by="tenant" />
                    <SortTh sort={sort} by="mode" />
                    <SortTh sort={sort} by="legacy" />
                    <SortTh sort={sort} by="bearer" />
                  </tr>
                </thead>
                {rows.map((t) => {
                  const linked = !!linkOf(links, t.tenantId).tenant;
                  return (
                    <tbody key={t.tenantId} className="nyk-row-group" data-tenant={t.tenantId}>
                      <tr>
                        <td className="nyk-card-title">
                          <TenantCell tenantId={t.tenantId} links={links} target={target} />
                        </td>
                        <td data-label="Sign-in">
                          <TenantAuthChip t={t} />
                        </td>
                        <td data-label="Last print queue token" title={formatDateTime(t.lastLegacyAt)}>
                          {when(t.lastLegacyAt)}
                        </td>
                        <td data-label="Last AUTH token" title={formatDateTime(t.lastBearerAt)}>
                          {when(t.lastBearerAt)}
                        </td>
                      </tr>
                      {canWrite && (
                        <ActionsRow colSpan={4}>
                          {t.mode === 'Legacy' && (
                            <Button size="sm" variant="ghost" onClick={() => void setMode(t, 'Both')}>
                              Allow AUTH
                            </Button>
                          )}
                          {t.mode !== 'Auth' && t.lastBearerAt && (
                            <ConfirmButton
                              size="sm"
                              variant="ghost"
                              message={`Require AUTH for ${t.tenantId}? Its print queue tokens stop working at once — the WorkServer's and every print server's. Only do this when all of them show up with AUTH tokens.`}
                              onConfirm={() => void setMode(t, 'Auth')}
                            >
                              Require AUTH
                            </ConfirmButton>
                          )}
                          {t.mode === 'Auth' && (
                            <Button size="sm" variant="ghost" onClick={() => void setMode(t, 'Both')}>
                              Allow tokens
                            </Button>
                          )}
                          {t.mode === 'Both' && (
                            <Button size="sm" variant="ghost" onClick={() => void setMode(t, 'Legacy')}>
                              Back to tokens
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={!linked || !signedIn(target)}
                            title={
                              !signedIn(target)
                                ? `Sign in to AUTH ${target} first: the client is created with your session.`
                                : !linked
                                  ? `Link the tenant first: alias printqueue:${t.tenantId} on its AUTH tenant.`
                                  : undefined
                            }
                            onClick={() => {
                              setError(null);
                              setClient({ tenantId: t.tenantId, server: '' });
                            }}
                          >
                            AUTH client for print server
                          </Button>
                        </ActionsRow>
                      )}
                    </tbody>
                  );
                })}
              </table>
            </TableScroller>
          )}
          {data.data && !rows.length && <EmptyState title="No tenants yet." />}
        </div>
      )}
      <Dialog
        open={!!client}
        onClose={() => setClient(null)}
        title={`AUTH client for a print server of ${client?.tenantId ?? ''}`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setClient(null)}>
              Cancel
            </Button>
            <Button loading={busy} onClick={() => void createClient()}>
              Create
            </Button>
          </>
        }
      >
        <div className="pq-form">
          <ErrorBox error={error} />
          <p>
            Creates the print server's own AUTH client (scope <b>printqueue:print</b>) in the tenant's AUTH
            tenant, or a new secret for an existing one.
          </p>
          <TextField
            label="Print server name"
            hint="As configured on the print server (PrintServerName)."
            value={client?.server ?? ''}
            onChange={(e) => client && setClient({ ...client, server: e.target.value })}
          />
        </div>
      </Dialog>
      {issued && <PrintServerClientDialog issued={issued} onClose={() => setIssued(null)} />}
    </Page>
  );
}

/** The print server's AUTH credentials, shown once. */
function PrintServerClientDialog({
  issued,
  onClose,
}: {
  issued: PrintServerClient & { tenantId: string; server: string };
  onClose: () => void;
}) {
  const lines = Object.entries(issued.environment)
    .map(([k, v]) => `${k}=${v}`)
    .join('\n');
  return (
    <Dialog
      open
      onClose={onClose}
      title={`AUTH client for ${issued.server}`}
      footer={<Button onClick={onClose}>Done</Button>}
    >
      <div className="pq-form">
        <p>
          Shown once. Set these on print server <b>{issued.server}</b> of {issued.tenantId} (environment, or{' '}
          <b>Auth:ClientId</b> / <b>Auth:ClientSecret</b> in its settings) and restart it; it needs print
          server 2.1.0 or later. Keep its <b>Token</b> until the tenant requires AUTH.
        </p>
        <pre className="pq-token" data-testid="pq-printserver-env">
          {lines}
        </pre>
        <Button size="sm" variant="secondary" onClick={() => void navigator.clipboard?.writeText(lines)}>
          Copy
        </Button>
      </div>
    </Dialog>
  );
}
